import React from 'react';
import PropTypes from 'prop-types';
import T from 'i18n-react';
import { shallow } from 'zustand/shallow';

import { LabelContainer } from '@motorway/motorway-component-library';

import CheckBox from 'Components/shared/CheckBox/CheckBox';
import ExpandableFilter from 'Components/shared/ExpandableFilter/ExpandableFilter';
import { VEHICLE_LIST_FILTER_NAME } from 'Components/VehicleList/VehicleListFilters.consts';
import { triggerAdditionalFiltersToggleClickEvent } from 'Services/analytics/events';
import useVehicleFilterStore from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';

import useFilterView from '../useFilterView';

import styles from '../Filters.module.scss';

T.setTexts(require('Components/texts.json'));

const AdditionalFiltersView = () => {
	const { isExpandableFilter } = useFilterView();
	const areInternalTestingFiltersEnabled = useFeatureToggle(FEATURES.marketplaceInternalTestingFilters);

	const [setValue, config, filters] = useVehicleFilterStore(
		(store) => [store.setValue, store.config, store.filters],
		shallow,
	);

	const title = T.translate('vehicleList.filters.additionalFilters.title');

	const {
		excludeVehiclesOnFinance,
		includeOnlyInternalTestingVehicles,
		includeSold,
		includeUnderOffer,
		isPaidListing,
	} = filters;

	const handleCheckboxChange = (filter) => {
		const nextValue = !filters[filter];
		triggerAdditionalFiltersToggleClickEvent({ filter, isToggleSelected: nextValue });
		setValue({ id: config[filter].id, value: nextValue });
	};

	const toggleConfig = (id) => ({
		id,
		onChange: () => handleCheckboxChange(id),
	});

	const toggles = [
		{
			checked: includeUnderOffer,
			...toggleConfig(VEHICLE_LIST_FILTER_NAME.INCLUDE_UNDER_OFFER),
			isHidden: !areInternalTestingFiltersEnabled,
			text: T.translate('vehicleList.includeUnderOffer'),
		},
		{
			checked: includeSold,
			...toggleConfig(VEHICLE_LIST_FILTER_NAME.INCLUDE_SOLD),
			isHidden: !areInternalTestingFiltersEnabled,
			text: T.translate('vehicleList.includeSold'),
		},
		{
			checked: !excludeVehiclesOnFinance,
			...toggleConfig(VEHICLE_LIST_FILTER_NAME.EXCLUDE_VEHICLES_ON_FINANCE),
			text: T.translate('vehicleList.filters.additionalFilters.includeVehiclesOnFinance'),
		},
		{
			checked: includeOnlyInternalTestingVehicles,
			isHidden: !areInternalTestingFiltersEnabled,
			...toggleConfig(VEHICLE_LIST_FILTER_NAME.INCLUDE_ONLY_INTERNAL_TESTING_VEHICLES),
			text: T.translate('vehicleList.filters.additionalFilters.includeOnlyInternalTestingVehicles'),
		},
		{
			checked: isPaidListing,
			...toggleConfig(VEHICLE_LIST_FILTER_NAME.IS_PAID_LISTING),
			text: T.translate('vehicleList.filters.additionalFilters.isPaidListing'),
		},
	];

	const Toggles = ({ className }) => (
		<div className={className}>
			{toggles.map((toggle) => (toggle?.isHidden ? null : <CheckBox key={toggle.id} {...toggle} />))}
		</div>
	);

	Toggles.propTypes = {
		className: PropTypes.string.isRequired,
	};

	return (
		<>
			{isExpandableFilter ? (
				<ExpandableFilter
					classNames={{
						content: styles.expandedAdditionalFilters,
						expanded: styles.expandedCheckBoxGroup,
					}}
					id="aditional-filters"
					isDirty={
						excludeVehiclesOnFinance ||
						includeSold ||
						includeUnderOffer ||
						includeOnlyInternalTestingVehicles ||
						isPaidListing
					}
					title={title}
				>
					{() => <Toggles className={styles.additionalFilterToggles} />}
				</ExpandableFilter>
			) : (
				<>
					<LabelContainer className="mw-mt-3 mw-mb-1" label={title} />
					<Toggles className="mw-mt-3" />
				</>
			)}
		</>
	);
};

export default AdditionalFiltersView;
